import { Injectable } from '@angular/core';
import { AppSettingService } from '@rd/core/services/app-setting/app-setting.service';
import { CurrencyService } from '@rd/core/services/currency/currency.service';
import { NumberService } from '@rd/core/services/number/number.service';
import { PointsNumberService } from '@rd/core/services/points-number/points-number.service';
import {
  CurrencyFormatRange,
  GiftCard,
  GiftCardCategory,
  GiftCardDenomination,
  GiftCardPhysicalAddressFormData,
  GiftCardType,
  LoyaltyCurrency,
} from '@rd/types';

const SECONDS_IN_A_DAY = 86400;

@Injectable({
  providedIn: 'root',
})
export class GiftCardHelper {
  constructor(
    protected appSettingService: AppSettingService,
    protected currencyService: CurrencyService,
    protected numberService: NumberService,
    private pointsNumberService: PointsNumberService,
  ) {}

  // TODO: It does not really depend on giftCard's Id it would change in the near future
  // validityInDays: -1 - No expire, BE api might return 0 in the future
  isFullyLoaded(giftCard?: GiftCard): boolean {
    return !!(
      giftCard &&
      giftCard.validityInDays !== null &&
      giftCard.validityInDays !== undefined
    );
  }

  giftCardImageUrl(giftCard: GiftCard): string | null {
    if (giftCard.imageUrls && giftCard.imageUrls.length > 0) {
      return giftCard.imageUrls[0];
    } else {
      return null;
    }
  }

  checkoutUrl(giftCard: GiftCard): string {
    const giftCardCheckoutUrl = '/gift_card/checkout/';
    return `${giftCardCheckoutUrl}${giftCard.id}`;
  }

  /*
  ====================================
    Translations
  ====================================
  */

  categoryTranslationKey(category: GiftCardCategory | 'all'): string {
    return `gift_card.listing.categories.${category}`;
  }

  denominationPriceInLoyaltyCurrencyParams(
    giftCardDenomination: GiftCardDenomination,
    loyaltyCurrencySelected: LoyaltyCurrency,
  ): { points_amount: string; currency: string } {
    const { price } = giftCardDenomination;

    return this.pointsAmountInLoyaltyCurrencyParams(
      price,
      loyaltyCurrencySelected,
    );
  }

  denominationDisplayValueWithCurrency(
    giftCardDenomination: GiftCardDenomination,
  ): string {
    return this.currencyService.formatCurrency(
      giftCardDenomination.displayValue,
      this.appSettingService.currencyFormat,
      '1.0-0',
    );
  }

  pointsAmountInLoyaltyCurrencyParams(
    value: number,
    loyaltyCurrencySelected: LoyaltyCurrency,
  ): { points_amount: string; currency: string } {
    return {
      points_amount: this.pointsNumberService.roundNumber(value),
      currency: loyaltyCurrencySelected.name,
    };
  }

  cashAmountWithCurrency(value: number): string {
    return this.currencyService.formatCurrency(
      value,
      this.appSettingService.currencyFormat,
    );
  }

  /*
  ====================================
    Listing page
  ====================================
  */

  minimumPoints(
    giftCard: GiftCard,
    loyaltyCurrencySelected: LoyaltyCurrency,
  ): string {
    const [lowestDisplayValue] = this.getMinMaxDenominationValues(
      giftCard.denominations,
      'price',
    );
    const minimumDenominationPrice =
      this.numberService.formatNumber(lowestDisplayValue);
    const minimumPoints = `${minimumDenominationPrice} ${loyaltyCurrencySelected.shortName}`;

    return minimumPoints;
  }

  displayedCurrencyCost(
    giftCard: GiftCard,
    currencyFormatRange: CurrencyFormatRange,
  ): string {
    if (this.shouldShowCurrencyRange(giftCard)) {
      return this.getCurrencyRange(giftCard, currencyFormatRange);
    } else {
      const [lowestDisplayValue] = this.getMinMaxDenominationValues(
        giftCard.denominations,
        'displayValue',
      );
      const formattedLowestDenomination = this.currencyService.formatCurrency(
        lowestDisplayValue,
        currencyFormatRange.lowest,
        '1.0-0',
      );

      return formattedLowestDenomination;
    }
  }

  // For denomination's displayValues, they're sorted with
  // type => displayValues with ascending order, so we need to find the min and max of both type combined.
  protected shouldShowCurrencyRange(giftCard: GiftCard): boolean {
    const [lowestDisplayValue, highestDisplayValue] =
      this.getMinMaxDenominationValues(giftCard.denominations, 'displayValue');

    return lowestDisplayValue !== highestDisplayValue;
  }

  // Values could be:
  // $100 - $1000, $100 - 1000, AUD 100 - 1000, AUD 100 - AUD 1000
  protected getCurrencyRange(
    giftCard: GiftCard,
    currencyFormatRange: CurrencyFormatRange,
  ): string {
    const [minDisplayValue, maxDisplayValue] = this.getMinMaxDenominationValues(
      giftCard.denominations,
      'displayValue',
    );

    const lowestDenomination = this.currencyService.formatCurrency(
      minDisplayValue,
      currencyFormatRange.lowest,
      '1.0-0',
    );
    const highestDenomination = this.currencyService.formatCurrency(
      maxDisplayValue,
      currencyFormatRange.highest,
      '1.0-0',
    );

    return `${lowestDenomination} - ${highestDenomination}`;
  }

  // This should only be used if we got from 2 displayTypes since data is
  // already sorted in ascending order.
  getMinMaxDenominationValues(
    denominations: GiftCardDenomination[],
    key: keyof GiftCardDenomination,
  ): number[] {
    const denominationDisplayValues = denominations.map((denomination) =>
      Number(denomination[key]),
    );

    return [
      Math.min(...denominationDisplayValues),
      Math.max(...denominationDisplayValues),
    ];
  }

  /*
  ====================================
    Checkout/details page terms list
  ====================================
  */

  deliveryMethodInfo(giftCard: GiftCard): string {
    return `gift_card.checkout.info.delivery_method.${giftCard.deliveryMethod}`;
  }

  validityDuration(giftCardDenomination: GiftCardDenomination): {
    translationKey: string;
    translationParams: { [key: string]: string | number };
  } {
    if (this.infiniteValidity(giftCardDenomination)) {
      return {
        translationKey: 'gift_card.checkout.info.validity_no_expiry',
        translationParams: {},
      };
    }

    return giftCardDenomination?.minValidityInDays
      ? {
          translationKey: 'gift_card.checkout.info.validity_min_duration',
          translationParams: {
            duration: this.validityDurationInfo({
              validityInDays: giftCardDenomination.minValidityInDays,
            }),
          },
        }
      : {
          translationKey: 'gift_card.checkout.info.validity_duration',
          translationParams: {
            duration: this.validityDurationInfo({
              validityInDays: giftCardDenomination.validityInDays,
            }),
          },
        };
  }

  validityDurationInfo(params: { validityInDays: number | null }): number {
    return params?.validityInDays
      ? params.validityInDays * SECONDS_IN_A_DAY
      : 0;
  }

  redemptionChannelInfo(giftCard: GiftCard): string {
    return `gift_card.checkout.info.redemption_channel.${giftCard.redemptionChannel}`;
  }

  infiniteValidity(params: { validityInDays: number }): boolean {
    return params?.validityInDays === -1;
  }

  displayTypeInfoAffix(giftCard: GiftCard): string {
    if (
      giftCard.displayTypes.includes(GiftCardType.Digital) &&
      giftCard.displayTypes.includes(GiftCardType.Physical)
    ) {
      return '.digital_and_physical';
    } else if (giftCard.displayTypes.includes(GiftCardType.Physical)) {
      return '.physical_only';
    }

    return '';
  }

  transformAddressFormData(
    physicalAddressForm: GiftCardPhysicalAddressFormData,
  ): Record<string, any> {
    const { addressLine1, addressLine2, city, postalCode, state, country } =
      physicalAddressForm;

    return {
      line_1: addressLine1,
      line_2: addressLine2,
      city,
      state,
      postalCode,
      countryCode: country,
    };
  }

  transformGiftCardAutoRedemptionAddressFormData(formData: {
    addressLine1: string;
    addressLine2: string | null;
    city: string;
    state: string;
    postalCode: string;
    countryCode: string | null;
  }): Record<string, any> {
    const { addressLine1, addressLine2, ...otherInputValue } = formData;
    return {
      ...otherInputValue,
      line_1: addressLine1,
      line_2: addressLine2,
    };
  }

  /*
    ====================================
  */
}
